/** Types de l'écran « Candidatures » (données lues de la base, déjà en camelCase). */

export type StatutPiece = 'valide' | 'rejete' | 'en_attente'

export const LIBELLE_DOC_LIVREUR: Record<string, string> = {
  national_id: "Pièce d'identité",
  criminal_record: 'Extrait de casier judiciaire',
  vehicle_registration: 'Carte grise',
  insurance: 'Assurance',
  photo: 'Photo',
  drivers_license: 'Permis de conduire',
  signed_contract: 'Contrat signé (PDF)',
}
export const LIBELLE_DOC_MARCHAND: Record<string, string> = {
  rccm: 'RCCM',
  nif: 'NIF',
  declaration_entreprenant: "Déclaration d'entreprenant",
  identite_gerant: "Pièce d'identité du gérant / de l'exploitant",
  photo_devanture: 'Photo de la devanture',
  autorisation_sanitaire: 'Autorisation sanitaire (si applicable)',
}
/** Pièces exigées d'un livreur (+ permis si `permis_requis`). */
export const PIECES_LIVREUR = ['national_id', 'criminal_record', 'vehicle_registration', 'insurance', 'photo'] as const
/** Pièces d'un marchand dont le statut n'est pas encore choisi (règle d'avant 0050) ; voir `piecesRequisesMarchand`. */
export const PIECES_MARCHAND = ['rccm', 'nif', 'identite_gerant', 'photo_devanture'] as const
export const PIECES_MARCHAND_FACULTATIVES = ['autorisation_sanitaire'] as const

export interface PieceDossier {
  id: string
  type: string
  libelle: string
  /** Chemin dans le bucket privé `onboarding` (jamais une URL). */
  chemin: string
  statut: StatutPiece
  valide: boolean
  motifRejet: string | null
  deposeLe: string | null
  /** Casier : date d'émission. */
  emisLe: string | null
  /** Assurance : échéance. */
  expireLe: string | null
}

export interface CandidatureLivreur {
  id: string
  nom: string
  telephone: string
  email: string
  vehicule: string
  statut: string
  motif: string | null
  permisRequis: boolean
  majLe: string | null
  creeLe: string | null
}

export interface CandidatureMarchand {
  id: string
  nom: string
  type: string
  quartier: string
  telephone: string
  emailGerant: string
  nomGerant: string
  proprietaireId: string | null
  statut: string
  motif: string | null
  majLe: string | null
  creeLe: string | null
}

export interface EntretienEnregistre {
  id: string
  version: string
  score: number
  elimine: boolean
  recommandation: 'retenir' | 'refuser' | 'revoir'
  notes: string | null
  date: string
}

export interface FormationEnregistree {
  id: string
  date: string
  formateur: string
  notes: string | null
}

export interface TentativeQuiz {
  id: string
  date: string
  scorePct: number
  bonnes: number
  total: number
  reussi: boolean
}

export interface SignatureContrat {
  id: string
  signataire: string
  modeleId: string
  version: number
  kind: 'livreur' | 'commercant'
  marchandId: string | null
  methode: 'app' | 'documenso'
  statut: 'envoye' | 'signe' | 'refuse' | 'expire'
  accepteLe: string | null
  creeLe: string
  lien: string | null
  email: string | null
  exclusiviteDebut: string | null
  exclusiviteFin: string | null
}

export interface EvenementJournal {
  id: number
  de: string | null
  vers: string
  motif: string | null
  auteur: string | null
  date: string
}

export interface CompteReversement {
  fournisseur: string
  telephone: string
  titulaire: string
  verifie: boolean
  verifieLe: string | null
}

/** Ligne d'identité (`merchant_identite` / `driver_identite`) : les champs tels que la base les range. */
export type LigneIdentite = Record<string, unknown>

export interface DossierLivreur {
  /** `null` : aucune identité saisie encore (compte d'avant 0050 ou candidature récente). */
  identite: LigneIdentite | null
  pieces: PieceDossier[]
  entretiens: EntretienEnregistre[]
  formations: FormationEnregistree[]
  tentatives: TentativeQuiz[]
  quizDeblocageLe: string | null
  signatures: SignatureContrat[]
  journal: EvenementJournal[]
}

export interface DossierMarchand {
  identite: LigneIdentite | null
  pieces: PieceDossier[]
  compte: CompteReversement | null
  signatures: SignatureContrat[]
  journal: EvenementJournal[]
}

export interface ModeleContrat {
  id: string
  kind: 'livreur' | 'commercant'
  version: number
  titre: string
  corps: string
  exclusiviteMois: number | null
  provisoire: boolean
  publieLe: string | null
}

/** Réponse de l'edge function `contrat-rendu` (aperçu du contrat rendu pour une partie). */
export interface ReponseContratRendu {
  ok: boolean
  apercu: boolean
  template_id: string
  version: number
  titre: string
  exclusivite_mois: number | null
  texte_md: string
  sha256: string
}

/** Réponse de l'edge function `envoyer-contrat-signature`. */
export interface ReponseEnvoiContrat {
  ok: boolean
  signature_id: string
  document_id: string
  lien_signature: string
  email_envoye: boolean
  telephone: string | null
  signataire_nom: string | null
  /** Un lien existait déjà : c'est lui qui est rendu (renvoyé par e-mail si demandé et possible). */
  deja_envoye?: boolean
  /** Renvoi par e-mail demandé mais impossible : contrat préparé sans vraie adresse (« adresse_technique »). */
  email_impossible?: string
}
